import { Component } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { JobStatusPillComponent } from 'src/app/shared/job-status-pill/job-status-pill.component';
import { selectJobId } from 'src/app/job/job-tool.reducer';
import { dayjs } from 'src/app/core/dayjs/dayjs';
import { strToTitleCase } from 'src/app/js';
import { brandingFeature } from 'src/app/state/branding.store';
import { LoadingState } from 'src/app/utilities/state.util';
import { SearchJobsQuery } from 'src/generated/graphql.generated';

import { NewOpportunitySimilarJobsActions } from './new-opportunity-similar-jobs.actions';
import { newOpportunitySimilarJobsFeature } from './new-opportunity-similar-jobs.feature';

export const SIMILAR_JOBS_DEFAULT = 2;
export const SIMILAR_JOBS_MAX = SIMILAR_JOBS_DEFAULT + 3;

type SimilarJob = NonNullable<SearchJobsQuery['searchJobs']['results'][number]>;

type SimilarJobRow = {
    id: string;
    url: string;
    zoneId: string | undefined;
    customerId: string | undefined;
    customer: string;
    code: string;
    relativeEdited: string;
    status: string;
    state: string | undefined;
    stage: string | undefined;
};

@Component({
    selector: 'app-new-opportunity-similar-jobs',
    standalone: true,
    imports: [
        FreyaCommonModule,
        JobStatusPillComponent,
    ],
    templateUrl: './new-opportunity-similar-jobs.component.html',
    styleUrls: ['./new-opportunity-similar-jobs.component.scss'],
})
export class NewOpportunitySimilarJobsComponent {

    viewModel$ = this.store.select(createSelector(
        newOpportunitySimilarJobsFeature.selectJobs,
        newOpportunitySimilarJobsFeature.selectLoading,
        newOpportunitySimilarJobsFeature.selectShowMore,
        newOpportunitySimilarJobsFeature.selectQuery,
        newOpportunitySimilarJobsFeature.selectPermissionDenied,
        selectJobId,
        brandingFeature.selectBranding,
        (jobs, loadingState, showMore, query, permissionDenied, jobId, { zoneId }) => ({
            showSection: !jobId && !permissionDenied,
            loading: jobs.length === 0 && loadingState === LoadingState.LOADING,
            rows: jobs.slice(0, showMore ? SIMILAR_JOBS_MAX : SIMILAR_JOBS_DEFAULT).map((j) => this.toRow(j)),
            showEmptyState: jobs.length === 0 && loadingState !== LoadingState.LOADING && loadingState !== LoadingState.INIT,
            showResults: jobs.length > 0,
            showMore,
            showMoreButton: jobs.length > SIMILAR_JOBS_DEFAULT || showMore,
            linkButtons: jobs.length > SIMILAR_JOBS_MAX && query
                ? [{
                    label: 'View All',
                    icon: 'pi pi-external-link',
                    routerLink: ['/jobs'],
                    queryParams: { search: query, zone: zoneId },
                }]
                : [],
        }),
    ));

    actions = NewOpportunitySimilarJobsActions;
    selectedRow: SimilarJobRow | null = null;
    showSelectDialog = false;

    constructor(public store: Store) {}

    trackById(_: number, row: SimilarJobRow) {
        return row.id;
    }

    private toRow(job: SimilarJob): SimilarJobRow {
        const customer = job.customerName || '--';
        const relativeEdited = this.formatRelative(job.createdAt);
        const status = this.getStatus(job);
        const zoneId = job.zoneId ?? undefined;

        return {
            id: job.id,
            url: `/jobs/${job.id}`,
            zoneId,
            customerId: job.customerId ?? undefined,
            customer,
            code: job.code || '--',
            relativeEdited,
            status,
            state: job.state ?? undefined,
            stage: job.stage ?? undefined,
        };
    }

    private getCustomerName(job: SimilarJob): string | null {
        return job.customerName || null;
    }

    private getStatus(job: SimilarJob): string {
        const state = job.state ? strToTitleCase(job.state) : '';
        const stage = job.stage ? strToTitleCase(job.stage) : '';

        if (state && stage) {
            return `${state} ${stage}`;
        }

        return state || stage || '--';
    }

    private formatRelative(timestamp: number | null | undefined): string {
        if (timestamp === null || timestamp === undefined) {
            return '--';
        }

        return dayjs(timestamp * 1000).fromNow();
    }
}
